import { ConditionSubject, TdbOverride, findOperator, findOperatorFor } from './tcdl-ops';

/**
 * Zeilen-Filter des BE-Schemas — ausgewertet von
 * `DataSheetRowSelector.java:82-94` (Server) und
 * `engine-core/src/engine/datasheet.ts:120-140` (Runner):
 *   {"type":"ROW_SET","expr":"2-5,8"}
 *   {"type":"VAR_MATCH","variable":"vBenutzer","value":"ZEMP_PS_01"}
 *   {"type":"VAR_MATCH","variable":"vAge","operator":"GREATER","value":"18"}
 *
 * Die UI arbeitet mit einem einzigen Freitext-Feld (`1;3-5;vUsername=Fritz&vAge>18`),
 * das erst beim Speichern in die strukturierten Eintraege uebersetzt wird. Jeder Eintrag
 * traegt seit NEX-2253 `group` (Nummer des `;`-Teils als Text): gleiche Gruppe = UND,
 * verschiedene = ODER, wie in Classic. Ein Teil mit `{…}`, der erst zur Laufzeit lesbar wird,
 * ist ein `{"type":"EXPR","expr":"{vZeile}","group":"0"}`. Bestandsdaten ohne `group` werten
 * Server und Runner wie vorher. Vergleiche
 * (`>`, `<`, `>=`, `<=`, `!=`) werden als `VAR_MATCH` mit gesetztem `operator`-Feld
 * abgelegt — der `=`-Fall bleibt kompatibel und schreibt den Operator NICHT explizit,
 * damit Legacy-Datenblatt-Filter unveraendert bleiben (sgraetz-Review NEX-1049).
 */
export type DataSheetFilterEntry = Record<string, string>;

/**
 * BE-Bedingung eines MODULE_REF-Schritts, Operator-Katalog in
 * `ConditionOperators.java:21-29`:
 *   {"type":"VALUE_COMPARE","operand":"{user1}","operator":"EQUAL","value":"Fritz"}
 *   {"type":"OBJECT_EXISTS","objectRef":"z01_SAP_dModalDialog.btnAbbrechen"}
 * Objekt-/Fenster-Bedingungen (meist aus Classic-Imports) sind seit NEX-1084 bearbeitbar.
 */
export type StepCondition = Record<string, string>;

/**
 * Zeilen-Grammatik, wortgleich mit `DataSheetRowSelector.ROW_SET` (Java) und
 * `engine-core/src/tcdl/parser.ts:65`. Nur was hier passt, darf als ROW_SET
 * geschrieben werden — beide Auswerter verwerfen alles andere, und zwar
 * gegensaetzlich: der Server plant dann ALLE Zeilen, der Runner faehrt KEINE.
 */
const ROW_SET_REGEX = /^\d+(?:\s*[-,]\s*\d+)*$/;

/** Ein Platzhalter `{vX}`, wie `PLATZHALTER` im Server. */
const PLACEHOLDER_REGEX = /\{[^{}]+\}/;

/** Nummer eines `;`-Teils; was nicht nur aus Ziffern besteht, gilt als fehlend. */
const GROUP_REGEX = /^\d+$/;

/** Ein einzelnes Segment innerhalb eines ROW_SET: `4` oder `3-5`. */
const ROW_INDEX_REGEX = /^\d+\s*(?:-\s*\d+)?$/;

/**
 * Vergleichsoperatoren im Freitext. Die Auswahl trifft {@link matchVarClause} nach
 * Position und Laenge, nicht nach Listenreihenfolge — an derselben Stelle gewinnt
 * {@code >=} gegen {@code >}, sonst wuerde der Wert als {@code =18} gelesen.
 */
const FILTER_OPERATORS: readonly { readonly sym: string; readonly code: string }[] = [
  { sym: '>=', code: 'GREATER_OR_EQUAL' },
  { sym: '<=', code: 'LESS_OR_EQUAL' },
  { sym: '!=', code: 'NOT_EQUAL' },
  { sym: '=', code: 'EQUAL' },
  { sym: '>', code: 'GREATER' },
  { sym: '<', code: 'LESS' },
];

const FILTER_SYMBOL_BY_CODE = new Map<string, string>(FILTER_OPERATORS.map((o) => [o.code, o.sym] as const));

/**
 * Zerlegt einen einzelnen Var-Ausdruck (`vAge>=18`) in Variable, Operator-Code
 * und Wert. Rueckgabe {@code null}, wenn kein bekannter Operator erkannt wurde.
 *
 * <p>Massgeblich ist der *linkeste* Operator im Text, bei gleicher Position der
 * laengere: `vName=a<=b` ist ein Gleichheitsvergleich auf `vName`, nicht ein
 * `<=`-Vergleich auf die Variable `vName=a`.
 */
function matchVarClause(clause: string): { variable: string; code: string; value: string } | null {
  let best: { sym: string; code: string; idx: number } | null = null;
  for (const op of FILTER_OPERATORS) {
    const idx = clause.indexOf(op.sym);
    if (idx <= 0) continue;
    if (!best || idx < best.idx || (idx === best.idx && op.sym.length > best.sym.length)) {
      best = { sym: op.sym, code: op.code, idx };
    }
  }
  if (!best) return null;
  return {
    variable: clause.slice(0, best.idx).trim(),
    code: best.code,
    value: clause.slice(best.idx + best.sym.length).trim(),
  };
}

/** Ergebnis von {@link parseFilterExpression}. */
export interface ParsedFilterExpression {
  readonly entries: DataSheetFilterEntry[];
  /**
   * Nicht deutbare Teilausdruecke, im Originaltext. Sie werden bewusst NICHT zu
   * Filtern — die anzeigende Stelle formuliert daraus ihren eigenen (uebersetzten)
   * Hinweis, damit hier kein deutscher Satz im TypeScript landet.
   */
  readonly warnings: string[];
}

/**
 * Parst den Freitext einer Zeilen-Auswahl in strukturierte Filter-Eintraege.
 * Leerer / weisser Text bedeutet „keine Einschraenkung". Einzelne `&`-getrennte
 * Var-Vergleichs-Bloecke werden eigenstaendige VAR_MATCH-Eintraege, damit das BE
 * sie unabhaengig auswerten kann; andere Vergleichsoperatoren als `=` schreiben
 * ihren BE-Code in `operator`.
 *
 * <p>Was weder der ROW_SET-Grammatik noch einem Var-Vergleich genuegt, kommt als
 * Warnung zurueck statt als Filter (sgraetz-Review NEX-1049) — genauso wie im
 * kanonischen Parser `engine-core/src/tcdl/parser.ts:126-178`.
 */
export function parseFilterExpression(expression: string | null | undefined): ParsedFilterExpression {
  const entries: DataSheetFilterEntry[] = [];
  const warnings: string[] = [];
  const trimmed = expression?.trim();
  if (!trimmed) return { entries, warnings };

  splitNonEmpty(trimmed, ';').forEach((part, index) => {
    const group = String(index);
    for (const clause of splitNonEmpty(part, '&')) {
      const entry = ROW_SET_REGEX.test(clause) ? { type: 'ROW_SET', expr: clause } : toVarMatch(clause);
      if (entry) {
        entries.push({ ...entry, group });
      } else if (PLACEHOLDER_REGEX.test(clause)) {
        // `{vZeile}` wird erst zur Laufzeit eine Zeilennummer — wie `parseDataSheetPins` im Parser.
        entries.push({ type: 'EXPR', expr: clause, group });
      } else {
        warnings.push(clause);
      }
    }
  });
  return { entries, warnings };
}

/**
 * Was beim Speichern aus dem Freitext wird. Steht im Feld noch genau der Text der
 * gespeicherten Bestandseintraege (ohne `group`), bleiben sie unveraendert. Sonst wuerde
 * `1;vStadt=Köln` (vorher UND) still zu ODER, sobald jemand nur die Bedingung aendert
 * (NEX-2253). Wer den Text aendert, bekommt die Classic-Bedeutung des Textes.
 */
export function filterEntriesFor(
  text: string | null | undefined,
  saved: readonly DataSheetFilterEntry[] | null | undefined,
): DataSheetFilterEntry[] {
  const bestand = saved ?? [];
  const unveraendert = (text?.trim() ?? '') === formatFilterExpression(bestand);
  if (unveraendert && !bestand.some((entry) => GROUP_REGEX.test(entry['group'] ?? ''))) return [...bestand];
  return parseFilterExpression(text).entries;
}

/** Ergebnis von {@link countSelectedRows}. */
export interface SelectedRowCount {
  readonly count: number;
  /** True, wenn Variablen-Bedingungen mitspielen — die greifen erst zur Ausfuehrung. */
  readonly bounded: boolean;
}

/**
 * Wie viele Zeilen laufen tatsaechlich? Zaehlt dieselben Nummern wie
 * `DataSheetRowSelector#expandRowSet` und `engine-core#expandRowSet`: Nummern
 * jenseits des Blatts fallen weg, Ueberschneidungen zaehlen einmal.
 *
 * <p>Liegt die Rechnung hier statt in der Anzeige, weil sie zur Grammatik gehoert
 * — vorher rechnete jede anzeigende Stelle eigenstaendig nach und `99` auf einem
 * 12-Zeilen-Blatt meldete „1 von 12", waehrend der Server keine Zeile waehlt.
 *
 * <p>Mit {@code active} zaehlen nur eingeschaltete Zeilen (NEX-2262), wie `selectRows` der Engine.
 */
export function countSelectedRows(
  total: number,
  filter: string | null | undefined,
  saved?: readonly DataSheetFilterEntry[] | null,
  active?: readonly boolean[] | null,
): SelectedRowCount {
  const { rows, bounded } = selectedRows(total, filter?.trim() ?? '', saved);
  return { count: [...rows].filter((row) => isActive(active, row)).length, bounded };
}

/** Zahl der Zeilen, die laufen duerfen (NEX-2262): ausgeschaltete zaehlen nicht, ohne Angabe sind alle an. */
export function activeRowCount(total: number, active?: readonly boolean[] | null): number {
  let count = 0;
  for (let row = 1; row <= total; row += 1) if (isActive(active, row)) count += 1;
  return count;
}

/** Zeilennummern der Auswahl, die das Blatt nicht hat — je Nummer einmal, in der Reihenfolge der Eingabe. */
export function missingRowNumbers(total: number, filter: string | null | undefined): number[] {
  const missing = new Set<number>();
  for (const clause of splitNonEmpty(filter ?? '', /[;&]/)) {
    if (!ROW_SET_REGEX.test(clause)) continue;
    for (const segment of splitNonEmpty(clause, ',')) {
      for (const bound of segment.split('-').map(Number)) if (bound > total) missing.add(bound);
    }
  }
  return [...missing];
}

function isActive(active: readonly boolean[] | null | undefined, row: number): boolean {
  return active?.[row - 1] !== false;
}

// Wie Server und Runner (NEX-2253): jeder `;`-Teil waehlt Zeilen dazu, `&` schraenkt im Teil ein.
// Ein Vergleich oder Platzhalter entscheidet erst im Lauf — der Teil zaehlt dann als Obergrenze.
function selectedRows(
  total: number,
  text: string,
  saved: readonly DataSheetFilterEntry[] | null | undefined,
): { rows: Set<number>; bounded: boolean } {
  if (saved && isUnchangedLegacy(text, saved)) return selectedRowsLegacy(total, text);
  const parts = splitNonEmpty(text, ';');
  if (!parts.length) return { rows: rowsOf(total, `1-${total}`), bounded: false };
  const rows = new Set<number>();
  let bounded = false;
  for (const part of parts) {
    let partRows: number[] | undefined;
    for (const clause of splitNonEmpty(part, '&')) {
      if (!ROW_SET_REGEX.test(clause)) {
        bounded = true;
        continue;
      }
      const clauseRows = rowsOf(total, clause);
      partRows = partRows ? partRows.filter((row: number) => clauseRows.has(row)) : [...clauseRows];
    }
    for (const row of partRows ?? rowsOf(total, `1-${total}`)) rows.add(row);
  }
  return { rows, bounded };
}

/** Gespeicherte Eintraege ohne `group`, deren Text unveraendert im Feld steht (siehe {@link filterEntriesFor}). */
function isUnchangedLegacy(text: string, saved: readonly DataSheetFilterEntry[]): boolean {
  return text === formatFilterExpression(saved) && !saved.some((entry) => GROUP_REGEX.test(entry['group'] ?? ''));
}

/** Zaehlweise vor NEX-2253: Zeilennummern vereinigt, jeder Vergleich macht daraus eine Obergrenze. */
function selectedRowsLegacy(total: number, text: string): { rows: Set<number>; bounded: boolean } {
  const parts = splitNonEmpty(text, /[;,]/);
  const indexParts = parts.filter((part) => ROW_INDEX_REGEX.test(part));
  const bounded = parts.some((part) => !ROW_INDEX_REGEX.test(part));
  if (!indexParts.length) return { rows: rowsOf(total, `1-${total}`), bounded };
  const rows = new Set<number>();
  for (const part of indexParts) for (const row of rowsOf(total, part)) rows.add(row);
  return { rows, bounded };
}

/** Die Zeilen eines ROW_SET-Ausdrucks (`2-5,8`), gedeckelt auf `1..total`. */
function rowsOf(total: number, expression: string): Set<number> {
  const rows = new Set<number>();
  for (const segment of splitNonEmpty(expression, ',')) {
    const [from, to] = segment.split('-').map(Number);
    const low = Math.max(1, Math.min(from, to ?? from));
    const high = Math.min(total, Math.max(from, to ?? from));
    for (let row = low; row <= high; row += 1) rows.add(row);
  }
  return rows;
}

function splitNonEmpty(text: string, separator: string | RegExp): string[] {
  return text
    .split(separator)
    .map((part) => part.trim())
    .filter(Boolean);
}

function toVarMatch(clause: string): DataSheetFilterEntry | null {
  const match = matchVarClause(clause);
  if (!match || !match.variable) return null;
  // `vUser=` heisst in Classic „die Zelle ist leer" und bleibt gueltig. Die
  // Ordnungsvergleiche brauchen dagegen einen Wert, sonst vergleicht das BE
  // lexikografisch gegen den leeren String und trifft jede belegte Zelle.
  if (match.code !== 'EQUAL' && !match.value) return null;
  const entry: DataSheetFilterEntry = {
    type: 'VAR_MATCH',
    variable: match.variable,
    value: match.value,
  };
  // Nur explizit setzen, wenn NICHT der Default `EQUAL` gemeint ist — sonst
  // aendert sich der Wire-Format-Vertrag fuer den heutigen Freitext `x=y`.
  if (match.code !== 'EQUAL') {
    entry['operator'] = match.code;
  }
  return entry;
}

/**
 * Formatiert die strukturierten Eintraege zurueck in Freitext, damit die UI beim
 * Reload den ursprunglichen Ausdruck (oder eine aequivalente Darstellung) zeigt.
 * Beruecksichtigt {@code operator} auf VAR_MATCH, sonst faellt `vAge>18` beim
 * Roundtrip auf `vAge=18` zurueck.
 */
export function formatFilterExpression(entries: readonly DataSheetFilterEntry[] | null | undefined): string {
  if (!entries?.length) return '';
  if (!entries.some((entry) => GROUP_REGEX.test(entry['group']?.trim() ?? ''))) return formatLegacy(entries);

  // Neue Form (NEX-2253): Gruppen aufsteigend mit `;`, in der Gruppe die Eintraege mit `&`.
  const groups = new Map<number, string[]>();
  for (const entry of entries) {
    const text = clauseOf(entry);
    if (!text) continue;
    const raw = entry['group']?.trim() ?? '';
    const key = GROUP_REGEX.test(raw) ? Number(raw) : Number.MAX_SAFE_INTEGER;
    groups.set(key, [...(groups.get(key) ?? []), text]);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a - b)
    .map(([, clauses]) => clauses.join('&'))
    .join(';');
}

/** Bestand ohne `group`: Zeilennummern zuerst mit `;`, dann die Vergleiche mit `&`. */
function formatLegacy(entries: readonly DataSheetFilterEntry[]): string {
  const rowSet = entries
    .filter((e) => e['type'] === 'ROW_SET')
    .map(clauseOf)
    .filter(Boolean);
  const varMatch = entries
    .filter((e) => e['type'] === 'VAR_MATCH')
    .map(clauseOf)
    .filter(Boolean);
  const parts: string[] = [...rowSet];
  if (varMatch.length) parts.push(varMatch.join('&'));
  return parts.join(';');
}

/** Ein Eintrag als Freitext-Teil; leer, wenn er unvollstaendig oder unbekannt ist. */
function clauseOf(entry: DataSheetFilterEntry): string {
  const type = entry['type'];
  if (type === 'ROW_SET' || type === 'EXPR') return entry['expr']?.trim() ?? '';
  if (type !== 'VAR_MATCH') return '';
  const variable = entry['variable']?.trim();
  if (!variable) return '';
  const value = entry['value']?.trim() ?? '';
  const opCode = entry['operator']?.trim();
  const sym = opCode ? (FILTER_SYMBOL_BY_CODE.get(opCode.toUpperCase()) ?? '=') : '=';
  return `${variable}${sym}${value}`;
}

/**
 * Objekt- und Fensterbedingungen: Art im BE (`StepConditionValidator`) je Subjekt und
 * Operator der Oberflaeche.
 */
const PRESENCE_KINDS: readonly { readonly type: string; readonly subj: ConditionSubject; readonly op: string }[] = [
  { type: 'OBJECT_EXISTS', subj: 'OBJEKT', op: 'EXISTS' },
  { type: 'OBJECT_NOT_EXISTS', subj: 'OBJEKT', op: 'NOT_EXISTS' },
  { type: 'WINDOW_EXISTS', subj: 'FENSTER', op: 'EXISTS' },
  { type: 'WINDOW_NOT_EXISTS', subj: 'FENSTER', op: 'NOT_EXISTS' },
  { type: 'OBJECT_VISIBLE', subj: 'OBJEKT', op: 'VISIBLE' },
  { type: 'OBJECT_NOT_VISIBLE', subj: 'OBJEKT', op: 'NOT_VISIBLE' },
  { type: 'OBJECT_ENABLED', subj: 'OBJEKT', op: 'ENABLED' },
  { type: 'OBJECT_NOT_ENABLED', subj: 'OBJEKT', op: 'NOT_ENABLED' },
  { type: 'WINDOW_VISIBLE', subj: 'FENSTER', op: 'VISIBLE' },
  { type: 'WINDOW_NOT_VISIBLE', subj: 'FENSTER', op: 'NOT_VISIBLE' },
];

/**
 * Baut aus den vier UI-Feldern die BE-Bedingung. Ohne Subjekt gibt es keine
 * Bedingung. Halbe Bedingungen (fehlender Bezug oder Operator, ein Operator, der nicht
 * zum Subjekt passt) landen ebenfalls als {@code null}, damit das BE nichts
 * stillschweigend behaelt, was es nicht ausfuehren kann (siehe
 * {@code StepConditionValidator}). Was der Aufrufer mit {@code null} macht (loeschen oder
 * die gespeicherte Bedingung behalten), entscheidet er.
 *
 * <p>{@link TdbOverride}.{@code op} traegt den Operator-Code, nie das lokalisierte
 * Label — sonst haengt die Bedingung an der eingestellten Sprache.
 */
export function toStepCondition(ov: TdbOverride | null | undefined): StepCondition | null {
  if (!ov || !ov.subj) return null;
  const operand = ov.ref?.trim();
  const opDef = findOperatorFor(ov.subj, ov.op);
  if (!operand || !opDef) return null;
  if (ov.subj !== 'WERT') {
    const kind = PRESENCE_KINDS.find((k) => k.subj === ov.subj && k.op === opDef.code);
    return kind ? { type: kind.type, objectRef: operand } : null;
  }
  const condition: StepCondition = {
    type: 'VALUE_COMPARE',
    operator: opDef.code,
    operand,
  };
  if (opDef.needsValue) {
    const value = ov.val?.trim() ?? '';
    if (!value) return null;
    condition['value'] = value;
  }
  return condition;
}

export type ConditionField = 'ref' | 'op' | 'val';

/**
 * Welche Felder einer begonnenen Bedingung fehlen, damit {@link toStepCondition} sie baut.
 * Ohne Subjekt gibt es keine Bedingung und damit nichts, was fehlt.
 */
export function missingConditionFields(ov: TdbOverride | null | undefined): ConditionField[] {
  if (!ov?.subj) return [];
  const opDef = findOperatorFor(ov.subj, ov.op);
  const missing: ConditionField[] = [];
  if (!ov.ref?.trim()) missing.push('ref');
  if (!opDef) missing.push('op');
  if (ov.subj === 'WERT' && opDef?.needsValue && !ov.val?.trim()) missing.push('val');
  return missing;
}

/**
 * Zerlegt eine BE-Bedingung in die vier UI-Felder: Wertvergleiche nach WERT, die vier
 * Objekt-/Fensterarten nach OBJEKT bzw. FENSTER. Unbekannte Arten liefern {@code {}}.
 *
 * <p>Akzeptiert alle BE-Aliase (siehe `TCDL_OPS.aliases` in `tcdl-ops.ts`) — vorher
 * fielen {@code EQUALS}, {@code GREATER_THAN}, {@code GRÖSSER}, {@code NICHTLEER}
 * stumm auf leere Bedingung und der Nutzer verlor sie beim naechsten Speichern
 * (sgraetz-Review NEX-1049).
 */
export function fromStepCondition(condition: StepCondition | null | undefined): TdbOverride {
  if (!condition) return {};
  const kind = art(condition);
  const presence = PRESENCE_KINDS.find((k) => k.type === kind);
  if (presence) {
    const ref = firstFilled(condition, 'objectRef', 'operand');
    return ref ? { subj: presence.subj, ref, op: presence.op } : {};
  }
  if (kind !== 'VALUE_COMPARE') return {};
  const operatorCandidate = condition['operator']?.trim() ?? '';
  const opDef = findOperator(operatorCandidate);
  const operand = (condition['operand'] ?? condition['objectRef'] ?? '').trim();
  if (!opDef || !operand) return {};
  const override: TdbOverride = {
    subj: 'WERT',
    ref: operand,
    op: opDef.code,
  };
  if (opDef.needsValue) {
    override.val = condition['value']?.trim() ?? '';
  }
  return override;
}

/** Bedingungen eines Schritts als Liste; Bestand mit nur `stepCondition` wird zur Liste mit einem Element. */
export function stepConditionsOf(step: {
  stepConditions?: readonly StepCondition[] | null;
  stepCondition?: StepCondition | null;
}): StepCondition[] {
  if (step.stepConditions) return [...step.stepConditions];
  return step.stepCondition ? [step.stepCondition] : [];
}

/** Zeilen der Bedingungsliste; was die Oberflaeche nicht lesen kann, bleibt verdeckt erhalten. */
export function fromStepConditions(conditions: readonly StepCondition[]): {
  rows: TdbOverride[];
  hidden: StepCondition[];
} {
  const rows: TdbOverride[] = [];
  const hidden: StepCondition[] = [];
  for (const condition of conditions) {
    const row = fromStepCondition(condition);
    if (row.subj) rows.push(row);
    else hidden.push(condition);
  }
  return { rows, hidden };
}

/**
 * Die Liste, die gespeichert wird. Eine unveraenderte Zeile sendet ihre gespeicherte Bedingung
 * samt Feldern, die die Oberflaeche nicht kennt (etwa `timeoutSeconds`); unvollstaendige Zeilen
 * fallen weg, verdeckte Bedingungen kommen hinten dazu.
 */
export function toStepConditions(rows: readonly TdbOverride[], saved: readonly StepCondition[]): StepCondition[] {
  const { hidden } = fromStepConditions(saved);
  const unused = saved.filter((condition) => !hidden.includes(condition));
  const result: StepCondition[] = [];
  for (const row of rows) {
    const built = toStepCondition(row);
    if (!built) continue;
    const index = unused.findIndex((condition) => sameCondition(toStepCondition(fromStepCondition(condition)), built));
    result.push(index >= 0 ? unused.splice(index, 1)[0] : built);
  }
  return [...result, ...hidden];
}

function sameCondition(a: StepCondition | null, b: StepCondition): boolean {
  if (!a) return false;
  const keys = Object.keys(b);
  return keys.length === Object.keys(a).length && keys.every((key) => a[key] === b[key]);
}

/** Erster nicht leere Wert der Felder, wie `StepConditionValidator#bezug`. */
function firstFilled(condition: StepCondition, ...keys: string[]): string {
  return keys.map((key) => condition[key]?.trim()).find(Boolean) ?? '';
}

/**
 * Welche Art eine Bedingung hat, entscheidet `StepConditionValidator#art` im Backend — dort
 * steht auch, warum. Hier dieselbe Regel: erster nicht leerer Wert von type, dann kind,
 * getrimmt und grossgeschrieben. Sonst zeigte die Oberflaeche eine Bedingung als
 * Wertvergleich, die Server und Runner als Objektbedingung lesen (NEX-1369).
 */
function art(condition: StepCondition): string {
  return (
    [condition['type'], condition['kind']]
      .map((wert) => wert?.trim())
      .find(Boolean)
      ?.toUpperCase() ?? ''
  );
}

const CONDITION_FIELDS: readonly (keyof TdbOverride)[] = ['subj', 'ref', 'op', 'val'];

/**
 * Welche Bedingung nach einer Eingabe gespeichert wird (NEX-1084). Eine Eingabe nur in der
 * Zeilen-Auswahl laesst die Bedingung unberuehrt — auch eine, die die Oberflaeche nicht
 * kennt. „— ohne —" loescht sie. Eine halbe Eingabe bleibt im Entwurf, gespeichert bleibt
 * bis zur vollstaendigen die bisherige Bedingung.
 */
export function nextStepCondition(
  saved: StepCondition | null | undefined,
  patch: Partial<TdbOverride>,
  next: TdbOverride,
): StepCondition | null {
  if (!CONDITION_FIELDS.some((field) => field in patch)) return saved ?? null;
  if (!next.subj) return null;
  return toStepCondition(next) ?? saved ?? null;
}
